import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Backdrop, Box, CircularProgress, useTheme } from '@mui/material';

interface LoaderContextType {
  toggleLoader: (key: string, toggle: boolean) => void;
}

const LoaderContext = createContext<LoaderContextType>({
  toggleLoader: () => {},
});

export const useLoader = () => useContext(LoaderContext);

interface LoaderProviderProps {
  children: ReactNode;
}

export const LoaderProvider: React.FC<LoaderProviderProps> = ({ children }) => {
  const [activeLoaders, setActiveLoaders] = useState<Set<string>>(new Set());
  const theme = useTheme();

  const toggleLoader = (key: string, toggle: boolean) => {
    setActiveLoaders((prev) => {
      const updatedLoaders = new Set(prev);
      if (toggle) {
        updatedLoaders.add(key);
      } else {
        updatedLoaders.delete(key);
      }
      return updatedLoaders;
    });
  };

  const isLoading = activeLoaders.size > 0;

  return (
    <LoaderContext.Provider value={{ toggleLoader }}>
      <Backdrop
        open={isLoading}
        sx={{
          position: 'fixed',
          height: '100vh',
          width: '100vw',
          top: 0,
          left: 0,
          backgroundColor: theme.palette.action.disabledBackground,
          zIndex: theme.zIndex.drawer + 1,
          color: theme.palette.primary.main,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            width: '100vw',
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      </Backdrop>
      {children}
    </LoaderContext.Provider>
  );
};
